import React from "react";
import { Box, Button, CircularProgress, Dialog, IconButton, LinearProgress, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import logoWithText from "../assets/images/headerlogo.svg";
import { parseGgFile, PREFLOP_ORDER, POSITION_LABELS } from "../analysis/ggParser";
import { analyzeHand, summarizeResults, AnalysisAccessError } from "../analysis/analyzerEngine";
import { openSubscriptionGate } from "../utils/billingAccess";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";

const COLORS = {
  bg: "#1a1a1a",
  cardBg: "#222222",
  panelBg: "#1f1f1f",
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#8f8f8f",
  white: "#ffffff",
  green: "#56ECCF",
  black: "#282828",
  danger: "#e57f7f",
};
const FUTURA_BOOK = "\"Futura Md Bt\", Futura, \"Trebuchet MS\", Arial, sans-serif";
const SUIT_COLORS = { s: "#888888", h: "#c44", d: "#48f", c: "#4a4" };
const SUIT_SYMBOLS = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };
const OUTCOME_COLORS = { best: COLORS.green, ok: COLORS.white, blunder: COLORS.danger, na: "#666666" };

function fmtBb(v, signed = true) {
  const n = Number(v || 0);
  const txt = Math.abs(n).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
  if (!signed) return txt;
  return n > 0 ? `+${txt}` : n < 0 ? `-${txt}` : "0";
}

function fmtDate(iso) {
  const d = new Date(iso || 0);
  if (!Number.isFinite(d.getTime()) || d.getTime() <= 0) return "-";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "numeric", year: "2-digit" });
}

const MiniCard = ({ card, size = 1 }) => (
  <Box
    sx={{
      width: 22 * size,
      height: 30 * size,
      backgroundColor: COLORS.black,
      border: `1px solid ${SUIT_COLORS[card?.[1]] || COLORS.border}`,
      borderRadius: "4px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: 11 * size, fontWeight: 700, color: SUIT_COLORS[card?.[1]] || COLORS.textMuted, lineHeight: 1 }}>
      {card?.[0] || "?"}
    </Typography>
    <Typography sx={{ fontSize: 8 * size, color: SUIT_COLORS[card?.[1]] || COLORS.textMuted, lineHeight: 1 }}>
      {SUIT_SYMBOLS[card?.[1]] || ""}
    </Typography>
  </Box>
);

// Large centered upload target: semi-transparent icon, text below, click or drag & drop.
const UploadDropZone = ({ onBrowse, dragActive, disabled }) => (
  <Box
    onClick={disabled ? undefined : onBrowse}
    sx={{
      minHeight: { xs: 320, sm: 420 },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.2,
      border: `2px dashed ${dragActive ? COLORS.green : COLORS.border}`,
      borderRadius: "18px",
      backgroundColor: dragActive ? "rgba(86,236,207,0.06)" : "rgba(255,255,255,0.015)",
      cursor: disabled ? "default" : "pointer",
      transition: "all 180ms ease",
      "&:hover": disabled ? {} : { borderColor: COLORS.green, backgroundColor: "rgba(86,236,207,0.04)" },
    }}
  >
    <UploadFileIcon sx={{ fontSize: { xs: 84, sm: 116 }, color: COLORS.green, opacity: dragActive ? 0.55 : 0.22, transition: "opacity 180ms ease" }} />
    <Typography sx={{ fontSize: { xs: 16, sm: 19 }, fontWeight: 800, color: COLORS.white, textAlign: "center" }}>
      {dragActive ? "Drop your file to analyse" : "Drag & drop your hand history here"}
    </Typography>
    <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted, textAlign: "center" }}>
      or click to browse (.txt exports, every decision graded vs GTO)
    </Typography>
  </Box>
);

const StatPill = ({ label, value, color }) => (
  <Box sx={{ backgroundColor: COLORS.panelBg, border: `1px solid ${COLORS.border}`, borderRadius: "10px", px: 1.6, py: 1, flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 19, fontWeight: 800, color: color || COLORS.white, lineHeight: 1.25, whiteSpace: "nowrap" }}>{value}</Typography>
  </Box>
);

function StatusDot({ hand }) {
  if (hand.status === "na") {
    return (
      <Box title={hand.offTreeReason || "N/A, off the GTO tree"} sx={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #555", display: "grid", placeItems: "center", flexShrink: 0 }}>
        <Typography sx={{ fontSize: 9, color: "#777", fontWeight: 800, lineHeight: 1 }}>-</Typography>
      </Box>
    );
  }
  const blunders = Number(hand.counts?.blunders || 0);
  const color = blunders > 0 ? COLORS.danger : COLORS.green;
  return (
    <Box
      title={blunders > 0 ? `${blunders} blunder${blunders > 1 ? "s" : ""}` : "GTO clean"}
      sx={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${color}`, display: "grid", placeItems: "center", flexShrink: 0 }}
    >
      <Typography sx={{ fontSize: 10, color, fontWeight: 800, lineHeight: 1 }}>{blunders > 0 ? "!" : "\u2713"}</Typography>
    </Box>
  );
}

// Per-street action letters, hero letters highlighted implicitly by color of result.
const StreetLetters = ({ txt }) => (
  <Typography sx={{ fontSize: 11.5, letterSpacing: "0.12em", color: COLORS.text, fontFamily: FUTURA_BOOK, whiteSpace: "nowrap" }}>
    {txt || ""}
  </Typography>
);

// ─── Replayer table visual ───────────────────────────────────────────────────

function ReplayerTable({ hand, step, foldedSet }) {
  const heroPos = hand.heroPos;
  const heroIdx = PREFLOP_ORDER.indexOf(heroPos);
  // Physical seats around an ellipse, hero pinned to the bottom.
  const seatAngles = [90, 150, 210, 270, 330, 30];
  const seats = PREFLOP_ORDER.map((pos, i) => {
    const angle = seatAngles[((i - heroIdx) % 6 + 6) % 6] * (Math.PI / 180);
    return {
      pos,
      isHero: pos === heroPos,
      left: 50 + 44 * Math.cos(angle),
      top: 50 + 42 * Math.sin(angle),
    };
  });
  const board = step?.boardShown || [];
  const actingPos = step?.kind === "action" ? step.pos : null;

  return (
    <Box sx={{ position: "relative", width: "100%", height: 250, mb: 1 }}>
      <Box
        sx={{
          position: "absolute",
          inset: "12% 8%",
          borderRadius: "999px",
          border: "8px solid #23201a",
          background: "radial-gradient(ellipse at 50% 42%, #14352b 0%, #0f2a22 55%, #0a1e18 100%)",
          boxShadow: "inset 0 0 40px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.18)",
        }}
      />
      {/* Board + pot */}
      <Box sx={{ position: "absolute", left: "50%", top: "44%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ display: "flex", gap: 0.5, minHeight: 34 }}>
          {board.map((c, i) => (
            <MiniCard key={`${c}-${i}`} card={c} size={1.15} />
          ))}
        </Box>
        <Typography sx={{ fontSize: 11.5, color: COLORS.white, fontWeight: 700 }}>
          Pot {Number(step?.potBb || 0).toFixed(1)}bb
        </Typography>
      </Box>
      {/* Seats */}
      {seats.map((s) => {
        const isActing = actingPos === s.pos;
        const isFolded = foldedSet.has(s.pos) && !isActing;
        return (
          <Box key={s.pos} sx={{ position: "absolute", left: `${s.left}%`, top: `${s.top}%`, transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4, opacity: isFolded ? 0.35 : 1 }}>
            {s.isHero && (
              <Box sx={{ display: "flex", gap: 0.3, mb: 0.2 }}>
                {(hand.heroCards || []).map((c, i) => (
                  <MiniCard key={i} card={c} />
                ))}
              </Box>
            )}
            <Box
              sx={{
                px: 1.2,
                py: 0.35,
                borderRadius: "999px",
                border: `2px solid ${isActing ? COLORS.green : s.isHero ? COLORS.white : "#3a3a3a"}`,
                backgroundColor: s.isHero ? "rgba(255,255,255,0.12)" : "#20201e",
                boxShadow: isActing ? `0 0 10px rgba(86,236,207,0.45)` : "none",
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: s.isHero ? COLORS.white : COLORS.text, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {POSITION_LABELS[s.pos]}
                {s.isHero ? " · HERO" : ""}
              </Typography>
            </Box>
            {isActing && step?.label && (
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: COLORS.green, whiteSpace: "nowrap" }}>{step.label}</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function StrategyPanel({ decision }) {
  if (!decision) return null;
  if (decision.outcome === "na" || !decision.pctMap) {
    return (
      <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", p: 1.4, backgroundColor: COLORS.panelBg }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#999" }}>N/A, no data</Typography>
        <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted, mt: 0.4 }}>
          {decision.reason || "This decision is off the GTO game tree."}
        </Typography>
      </Box>
    );
  }
  const rows = Object.entries(decision.pctMap || {}).sort((a, b) => b[1] - a[1]);
  return (
    <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", p: 1.4, backgroundColor: COLORS.panelBg }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: OUTCOME_COLORS[decision.outcome] || COLORS.text }}>
          {decision.outcome === "best" ? "Best play" : decision.outcome === "ok" ? "GTO mix" : "Blunder"}
          {" · "}GTO score {decision.score}
        </Typography>
        {Number.isFinite(Number(decision.ev)) && decision.ev != null && (
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.textMuted }}>
            GTO EV {Number(decision.ev).toFixed(2)}bb
          </Typography>
        )}
      </Box>
      {rows.map(([label, pct]) => {
        const chosen = label === decision.chosenLabel;
        const barColor = chosen ? OUTCOME_COLORS[decision.outcome] : "#3d5a52";
        return (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: chosen ? 800 : 600, color: chosen ? COLORS.white : COLORS.text, width: 86, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {label}
            </Typography>
            <Box sx={{ flex: 1, height: 9, backgroundColor: "#2a2a2a", borderRadius: 999, overflow: "hidden" }}>
              <Box sx={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: "100%", backgroundColor: barColor, borderRadius: 999 }} />
            </Box>
            <Typography sx={{ fontSize: 11, color: COLORS.textMuted, width: 44, textAlign: "right" }}>{Number(pct).toFixed(1)}%</Typography>
            <Box sx={{ width: 18, textAlign: "center" }}>
              {chosen && (
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: OUTCOME_COLORS[decision.outcome], lineHeight: 1 }}>
                  {decision.outcome === "blunder" ? "\u2717" : "\u2713"}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
      <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, mt: 0.6 }}>
        You: {decision.chosenLabel} ({decision.chosenPct}%) · GTO best: {decision.bestLabel} ({decision.bestPct}%)
      </Typography>
    </Box>
  );
}

function HandReplayer({ handSummary, onClose }) {
  const [detail, setDetail] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [stepIdx, setStepIdx] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/analysis/hands/${encodeURIComponent(handSummary.handId)}`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.success) setDetail(data.hand);
        else setLoadError(data?.error || "Failed to load hand.");
      } catch {
        if (!cancelled) setLoadError("Failed to load hand.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handSummary.handId]);

  // Precompute cumulative board + folded seats for every step.
  const enrichedSteps = React.useMemo(() => {
    if (!detail?.steps) return [];
    let board = [];
    const folded = new Set();
    return detail.steps.map((s) => {
      if (s.kind === "board" && Array.isArray(s.board)) board = s.board;
      if (s.kind === "action" && /^fold/i.test(String(s.label || ""))) folded.add(s.pos);
      return { ...s, boardShown: [...board], foldedSet: new Set(folded) };
    });
  }, [detail]);

  const step = enrichedSteps[stepIdx] || null;
  const decision = step?.decisionIdx != null ? detail?.decisions?.[step.decisionIdx] : null;
  const maxIdx = enrichedSteps.length - 1;

  const jump = (delta) => setStepIdx((i) => Math.max(0, Math.min(maxIdx, i + delta)));

  // Keyboard navigation.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setStepIdx((i) => Math.min(maxIdx, i + 1));
      if (e.key === "ArrowLeft") setStepIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maxIdx]);

  const streetGroups = React.useMemo(() => {
    const groups = [];
    enrichedSteps.forEach((s, idx) => {
      if (s.kind !== "action") return;
      let g = groups.find((x) => x.street === s.street);
      if (!g) {
        g = { street: s.street, items: [] };
        groups.push(g);
      }
      g.items.push({ idx, step: s, decision: s.decisionIdx != null ? detail?.decisions?.[s.decisionIdx] : null });
    });
    return groups;
  }, [enrichedSteps, detail]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", backgroundImage: "none" } }}
    >
      <Box sx={{ p: { xs: 1.4, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatusDot hand={handSummary} />
            <Box sx={{ display: "flex", gap: 0.4 }}>
              {(handSummary.heroCards || []).map((c, i) => (
                <MiniCard key={i} card={c} />
              ))}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: COLORS.white }}>
              {POSITION_LABELS[handSummary.heroPos] || "?"} · {handSummary.stakes} · {handSummary.potType}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: COLORS.textMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loadError && <Typography sx={{ fontSize: 12.5, color: COLORS.danger, py: 3, textAlign: "center" }}>{loadError}</Typography>}
        {!detail && !loadError && (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress size={26} sx={{ color: COLORS.green }} />
          </Box>
        )}

        {detail && (
          <>
            <ReplayerTable hand={detail} step={step} foldedSet={step?.foldedSet || new Set()} />

            {/* Step controls */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.6, mb: 1 }}>
              <IconButton size="small" onClick={() => setStepIdx(0)} disabled={stepIdx <= 0} sx={{ color: COLORS.text }}>
                <FirstPageIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => jump(-1)} disabled={stepIdx <= 0} sx={{ color: COLORS.text }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted, minWidth: 120, textAlign: "center" }}>
                {step ? `${step.street === "result" ? "Result" : step.street.toUpperCase()} · step ${stepIdx + 1}/${enrichedSteps.length}` : ""}
              </Typography>
              <IconButton size="small" onClick={() => jump(1)} disabled={stepIdx >= maxIdx} sx={{ color: COLORS.text }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setStepIdx(maxIdx)} disabled={stepIdx >= maxIdx} sx={{ color: COLORS.text }}>
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Current step label / strategy */}
            {step?.kind === "action" && step.isHero && decision ? (
              <StrategyPanel decision={decision} />
            ) : (
              <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", p: 1.2, backgroundColor: COLORS.panelBg }}>
                <Typography sx={{ fontSize: 12, color: COLORS.text }}>
                  {step?.kind === "action" ? `${POSITION_LABELS[step.pos] || "?"} ${step.label}` : step?.label || ""}
                </Typography>
              </Box>
            )}

            {/* Per-street summary (click to jump) */}
            <Box sx={{ mt: 1.2, display: "flex", flexDirection: "column", gap: 0.7 }}>
              {streetGroups.map((g) => (
                <Box key={g.street} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", px: 1.2, py: 0.8, backgroundColor: COLORS.cardBg }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", width: 52 }}>
                      {g.street}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.3 }}>
                      {(g.street === "flop" ? (detail.board || []).slice(0, 3)
                        : g.street === "turn" ? (detail.board || []).slice(3, 4)
                        : g.street === "river" ? (detail.board || []).slice(4, 5)
                        : []
                      ).map((c, i) => (
                        <MiniCard key={i} card={c} size={0.8} />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {g.items.map(({ idx, step: s, decision: d }) => {
                      const active = idx === stepIdx;
                      const color = d ? OUTCOME_COLORS[d.outcome] : s.isHero ? COLORS.white : "#555";
                      return (
                        <Box
                          key={idx}
                          onClick={() => setStepIdx(idx)}
                          sx={{
                            px: 0.9,
                            py: 0.3,
                            borderRadius: "999px",
                            cursor: "pointer",
                            border: `1px solid ${active ? COLORS.white : color}`,
                            backgroundColor: s.isHero ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap" }}>
                            {POSITION_LABELS[s.pos] || "?"} {s.label}
                          </Typography>
                          {d && (
                            <Typography sx={{ fontSize: 10.5, fontWeight: 900, color, lineHeight: 1 }}>
                              {d.outcome === "na" ? "N/A" : d.outcome === "blunder" ? "\u2717" : "\u2713"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Result line */}
            <Box sx={{ mt: 1.2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted }}>
                {detail.status === "na"
                  ? `N/A (no data): ${detail.offTreeReason || "off the GTO tree"}`
                  : detail.offTreeReason
                    ? `Partially analyzed: ${detail.offTreeReason} (${detail.offTreeStreet})`
                    : `Fully analyzed · GTO score ${detail.gtoScore ?? "-"}`}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: Number(detail.winLossBb) >= 0 ? COLORS.green : COLORS.danger }}>
                {fmtBb(detail.winLossBb)}bb
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}

// ─── Locked-state preview data (blurred teaser behind the Pro lock card) ─────

const LOCKED_PREVIEW_SUMMARY = {
  totalHands: 438,
  analyzedHands: 402,
  naHands: 36,
  avgGtoScore: 84,
  totalDecisions: 1187,
  best: 941,
  ok: 178,
  blunders: 68,
  winLossBb: 212.4,
  byStreet: {
    preflop: { decisions: 872, best: 731, blunders: 34, avgScore: 88 },
    flop: { decisions: 164, best: 118, blunders: 14, avgScore: 79 },
    turn: { decisions: 92, best: 58, blunders: 11, avgScore: 71 },
    river: { decisions: 59, best: 34, blunders: 9, avgScore: 66 },
  },
};

const LOCKED_PREVIEW_HANDS = [
  { handId: "PV1", status: "analyzed", heroCards: ["Ah", "Kd"], heroPos: "co", potType: "3Bet", playedAt: "2026-07-18T20:14:00Z", counts: { blunders: 2 }, winLossBb: -42.5 },
  { handId: "PV2", status: "analyzed", heroCards: ["Td", "Ts"], heroPos: "btn", potType: "SRP", playedAt: "2026-07-18T19:41:00Z", counts: { blunders: 1 }, winLossBb: 18.2 },
  { handId: "PV3", status: "analyzed", heroCards: ["Qs", "Jh"], heroPos: "bb", potType: "SRP", playedAt: "2026-07-17T22:03:00Z", counts: { blunders: 1 }, winLossBb: -12.8 },
  { handId: "PV4", status: "analyzed", heroCards: ["9c", "8c"], heroPos: "sb", potType: "3Bet", playedAt: "2026-07-17T21:26:00Z", counts: { blunders: 1 }, winLossBb: -25.0 },
];

// ─── Main page ───────────────────────────────────────────────────────────────

export default function Analysis() {
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);
  const [accessChecked, setAccessChecked] = React.useState(false);
  const [hasAccess, setHasAccess] = React.useState(false);
  const [view, setView] = React.useState("results"); // results | hands
  const [hands, setHands] = React.useState([]);
  const [loadingHands, setLoadingHands] = React.useState(true);
  const [progress, setProgress] = React.useState(null); // {phase, done, total, note}
  const [uploadSummary, setUploadSummary] = React.useState(null);
  const [openHand, setOpenHand] = React.useState(null);
  const cancelRef = React.useRef(false);
  const [dragActive, setDragActive] = React.useState(false);
  const dragDepthRef = React.useRef(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/billing/status`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          const features = data?.billing?.hasFeatureAccess;
          setHasAccess(features ? !!features.analyse : !!data?.billing?.hasAccess && data?.billing?.plan === "pro");
        }
      } catch (_) {}
      if (!cancelled) setAccessChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshHands = React.useCallback(async () => {
    setLoadingHands(true);
    try {
      const res = await fetch(`${SOLVER_API_URL}/api/analysis/hands?limit=1000`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) setHands(Array.isArray(data.hands) ? data.hands : []);
    } catch (_) {}
    setLoadingHands(false);
  }, []);

  React.useEffect(() => {
    if (accessChecked && hasAccess) refreshHands();
  }, [accessChecked, hasAccess, refreshHands]);

  const saveBatch = React.useCallback(async (batch) => {
    if (!batch.length) return;
    await fetch(`${SOLVER_API_URL}/api/analysis/hands/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ hands: batch }),
    });
  }, []);

  const handleFile = React.useCallback(async (file) => {
    if (!file) return;
    cancelRef.current = false;
    setUploadSummary(null);
    setProgress({ phase: "parsing", done: 0, total: 0, note: file.name });
    let parsed;
    try {
      const text = await file.text();
      parsed = parseGgFile(text);
    } catch {
      setProgress(null);
      setUploadSummary({ error: "Could not read that file. Please upload a .txt hand history export." });
      return;
    }
    const { eligible, skipped, parseErrors, totalHands } = parsed;
    if (!eligible.length) {
      setProgress(null);
      setUploadSummary({ totalHands, analyzed: 0, na: 0, failed: 0, skipped, parseErrors });
      return;
    }

    let batch = [];
    let analyzedCount = 0;
    let naCount = 0;
    let failed = 0;
    for (let i = 0; i < eligible.length; i++) {
      if (cancelRef.current) break;
      setProgress({ phase: "analyzing", done: i, total: eligible.length, note: `Hand ${eligible[i].handId}` });
      try {
        const result = await analyzeHand(eligible[i]);
        if (result.status === "analyzed") analyzedCount += 1;
        else naCount += 1;
        batch.push(result);
      } catch (err) {
        if (err instanceof AnalysisAccessError) {
          await saveBatch(batch);
          setProgress(null);
          setUploadSummary({ error: err.code === "PAYWALL" ? "Your Pro subscription is required to analyze hands." : "Please log in again to continue." });
          return;
        }
        failed += 1;
      }
      if (batch.length >= 50) {
        setProgress({ phase: "saving", done: i + 1, total: eligible.length, note: "Saving..." });
        await saveBatch(batch).catch(() => {});
        batch = [];
      }
    }
    if (batch.length) await saveBatch(batch).catch(() => {});
    setProgress(null);
    setUploadSummary({ totalHands, analyzed: analyzedCount, na: naCount, failed, skipped, parseErrors, cancelled: cancelRef.current });
    refreshHands();
  }, [refreshHands, saveBatch]);

  const summary = React.useMemo(() => summarizeResults(hands), [hands]);

  // Page-wide drag & drop: dropping a .txt anywhere on the page starts an upload.
  const acceptDrop = hasAccess && !progress;
  const onPageDragEnter = (e) => {
    if (!acceptDrop || !Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  };
  const onPageDragOver = (e) => {
    if (!acceptDrop) return;
    e.preventDefault();
  };
  const onPageDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };
  const onPageDrop = (e) => {
    if (!acceptDrop) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!/\.txt$/i.test(file.name || "")) {
      setUploadSummary({ error: "Please drop a .txt hand history export." });
      return;
    }
    handleFile(file);
  };

  const navTabSx = (active) => ({
    minWidth: "unset",
    px: { xs: 1.6, sm: 2.1 },
    py: { xs: 0.45, sm: 0.6 },
    borderRadius: "999px",
    border: "none",
    backgroundColor: active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.02)",
    color: active ? COLORS.white : COLORS.textMuted,
    fontSize: { xs: "11px", sm: "12px" },
    fontWeight: active ? 700 : 600,
    fontFamily: FUTURA_BOOK,
    textTransform: "none",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    transition: "all 160ms ease",
    "&:hover": { color: COLORS.white, backgroundColor: active ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.07)" },
  });

  const viewTabSx = (active) => ({
    textTransform: "none",
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: FUTURA_BOOK,
    px: 2,
    borderRadius: "999px",
    color: active ? "#111" : COLORS.text,
    backgroundColor: active ? COLORS.green : "rgba(255,255,255,0.04)",
    "&:hover": { backgroundColor: active ? COLORS.green : "rgba(255,255,255,0.1)" },
  });

  return (
    <Box
      onDragEnter={onPageDragEnter}
      onDragOver={onPageDragOver}
      onDragLeave={onPageDragLeave}
      onDrop={onPageDrop}
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        fontFamily: FUTURA_BOOK,
        "& .MuiButton-root": { fontFamily: FUTURA_BOOK },
      }}
    >
      {/* Header */}
      <Box sx={{ py: 1.5, px: 2, pl: { xs: 1.2, sm: 3 }, pr: { xs: 14, sm: 2 }, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.6, sm: 1.2 }, minWidth: 0, flex: 1, overflowX: "auto" }}>
          <img src={logoWithText} alt="PokerMoose" onClick={() => navigate("/")} style={{ height: 32, width: 32, flexShrink: 0, opacity: 0.5, filter: "grayscale(1)", cursor: "pointer" }} />
          <Typography sx={{ display: { xs: "none", sm: "inline" }, color: COLORS.white, fontSize: "13px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "Play, Arial, sans-serif", lineHeight: 1, mr: 0.4, userSelect: "none", flexShrink: 0 }}>
            PokerMoose
          </Typography>
          <Button variant="text" size="small" onClick={() => navigate("/")} sx={{ ...navTabSx(false), flexShrink: 0 }}>Home</Button>
          <Button variant="text" size="small" onClick={() => navigate("/cash")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Solve</Button>
          <Button variant="text" size="small" onClick={() => navigate("/trainer")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Train</Button>
          <Button variant="text" size="small" onClick={() => navigate("/play")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Play</Button>
          <Button variant="text" size="small" onClick={() => navigate("/analysis")} sx={{ ...navTabSx(true), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Analyze</Button>
        </Box>
        <Box />
      </Box>

      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 1.4, sm: 2.5 }, py: { xs: 2, sm: 3 } }}>
        {!accessChecked ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
            <CircularProgress size={30} sx={{ color: COLORS.green }} />
          </Box>
        ) : !hasAccess ? (
          <Box sx={{ position: "relative" }}>
            {/* Blurred preview of a full dashboard; teaser for the Pro upgrade. */}
            <Box
              aria-hidden
              sx={{
                filter: "blur(6px)",
                pointerEvents: "none",
                userSelect: "none",
                opacity: 0.85,
                minHeight: 560,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.6, mb: 2 }}>
                <Typography className="pm-title" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: COLORS.white }}>Analyze</Typography>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                  <Button size="small" sx={viewTabSx(true)}>Results</Button>
                  <Button size="small" sx={viewTabSx(false)}>Hands</Button>
                </Box>
              </Box>
              <ResultsView summary={LOCKED_PREVIEW_SUMMARY} hands={LOCKED_PREVIEW_HANDS} loading={false} onSeeHands={() => {}} />
            </Box>
            {/* Lock card overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: { xs: 8, sm: 12 },
                background: "linear-gradient(180deg, rgba(26,26,26,0.25) 0%, rgba(26,26,26,0.65) 60%, rgba(26,26,26,0.9) 100%)",
              }}
            >
              <Box sx={{ maxWidth: 460, mx: 2, textAlign: "center", backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.white}`, borderRadius: "14px", p: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.55)" }}>
                <LockOutlinedIcon sx={{ color: COLORS.white, fontSize: 34, mb: 1 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.white, mb: 0.8 }}>
                  Hand Analysis is a Pro feature
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.6, mb: 2 }}>
                  Upload your real hands and get a GTO grade on every decision, preflop to river, with mistakes and blunders flagged. Upgrade to Pro to unlock it.
                </Typography>
                <Button
                  onClick={() => openSubscriptionGate("analyse", "pro")}
                  sx={{ textTransform: "none", fontSize: 13, fontWeight: 800, color: "#111", backgroundColor: COLORS.green, borderRadius: "999px", px: 3, py: 0.8, "&:hover": { backgroundColor: "rgb(45, 179, 155)" } }}
                >
                  Subscribe to Pro Now
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {/* Title row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.2, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
                <Typography className="pm-title" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: COLORS.white }}>Analyze</Typography>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                  <Button size="small" onClick={() => setView("results")} sx={viewTabSx(view === "results")}>Results</Button>
                  <Button size="small" onClick={() => setView("hands")} sx={viewTabSx(view === "hands")}>Hands</Button>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    handleFile(f);
                  }}
                />
                <Button
                  startIcon={<UploadFileIcon />}
                  disabled={!!progress}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 800, color: "#111", backgroundColor: COLORS.green, borderRadius: "999px", px: 2.2, "&:hover": { backgroundColor: "rgb(45, 179, 155)" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#666" } }}
                >
                  Upload hands (.txt)
                </Button>
              </Box>
            </Box>

            {/* Upload progress */}
            {progress && (
              <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "12px", p: 1.6, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.white }}>
                    {progress.phase === "parsing"
                      ? "Reading file..."
                      : progress.phase === "saving"
                        ? "Saving results..."
                        : `Analyzing hand ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      cancelRef.current = true;
                    }}
                    sx={{ textTransform: "none", fontSize: 11.5, color: COLORS.danger }}
                  >
                    Stop
                  </Button>
                </Box>
                <LinearProgress
                  variant={progress.total > 0 ? "determinate" : "indeterminate"}
                  value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
                  sx={{ height: 7, borderRadius: 999, backgroundColor: "#2a2a2a", "& .MuiLinearProgress-bar": { backgroundColor: COLORS.green, borderRadius: 999 } }}
                />
                <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, mt: 0.6 }}>
                  Preflop grades locally; postflop hands query the GTO tree, so bigger files take a few minutes. Keep this tab open.
                </Typography>
              </Box>
            )}

            {/* Upload result summary */}
            {uploadSummary && (
              <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${uploadSummary.error ? COLORS.danger : COLORS.border}`, borderRadius: "12px", p: 1.6, mb: 2 }}>
                {uploadSummary.error ? (
                  <Typography sx={{ fontSize: 12.5, color: COLORS.danger }}>{uploadSummary.error}</Typography>
                ) : (
                  <>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.white, mb: 0.4 }}>
                      Upload complete{uploadSummary.cancelled ? " (stopped early)" : ""}: {uploadSummary.analyzed} analyzed, {uploadSummary.na} N/A (off-tree), {uploadSummary.skipped.length} skipped
                      {uploadSummary.failed > 0 ? `, ${uploadSummary.failed} failed` : ""}
                    </Typography>
                    {uploadSummary.skipped.length > 0 && (
                      <Typography sx={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
                        Skipped: {Object.entries(
                          uploadSummary.skipped.reduce((acc, s) => {
                            const key = s.reason.replace(/\d+bb/, "Xbb");
                            acc[key] = (acc[key] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([reason, count]) => `${reason} (${count})`).join(" · ")}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}

            {!loadingHands && hands.length === 0 ? (
              <UploadDropZone onBrowse={() => fileInputRef.current?.click()} dragActive={dragActive} disabled={!!progress} />
            ) : view === "results" ? (
              <ResultsView summary={summary} hands={hands} loading={loadingHands} onSeeHands={() => setView("hands")} />
            ) : (
              <HandsListView hands={hands} loading={loadingHands} onOpen={setOpenHand} />
            )}
          </>
        )}
      </Box>

      {/* Full-page drop hint while dragging over existing results */}
      {dragActive && hands.length > 0 && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 1500, pointerEvents: "none", display: "grid", placeItems: "center", backgroundColor: "rgba(26,26,26,0.72)" }}>
          <Box sx={{ border: `2px dashed ${COLORS.green}`, borderRadius: "18px", px: 6, py: 4, textAlign: "center", backgroundColor: "rgba(34,34,34,0.92)" }}>
            <UploadFileIcon sx={{ fontSize: 72, color: COLORS.green, opacity: 0.5 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: COLORS.white, mt: 1 }}>
              Drop your .txt file to analyse
            </Typography>
          </Box>
        </Box>
      )}

      {openHand && <HandReplayer handSummary={openHand} onClose={() => setOpenHand(null)} />}
    </Box>
  );
}

// ─── Results dashboard ───────────────────────────────────────────────────────

function ResultsView({ summary, hands, loading, onSeeHands }) {
  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: COLORS.green }} />
      </Box>
    );
  }
  if (!summary.totalHands) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.white, mb: 0.6 }}>No hands yet</Typography>
        <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted }}>
          Upload a hand history file (.txt) and every decision gets graded against GTO.
        </Typography>
      </Box>
    );
  }
  const scoreColor = summary.avgGtoScore == null ? COLORS.textMuted : summary.avgGtoScore >= 80 ? COLORS.green : summary.avgGtoScore >= 55 ? COLORS.white : COLORS.danger;
  const blunderRows = hands
    .filter((h) => h.status === "analyzed" && Number(h.counts?.blunders || 0) > 0)
    .slice(0, 6);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
        <StatPill label="Hands analyzed" value={summary.analyzedHands} />
        <StatPill label="GTO score" value={summary.avgGtoScore == null ? "-" : summary.avgGtoScore} color={scoreColor} />
        <StatPill label="Decisions graded" value={summary.totalDecisions} />
        <StatPill label="Blunders" value={summary.blunders} color={summary.blunders > 0 ? COLORS.danger : COLORS.green} />
        <StatPill label="Win/Loss" value={`${fmtBb(summary.winLossBb)}bb`} color={summary.winLossBb >= 0 ? COLORS.green : COLORS.danger} />
        <StatPill label="N/A hands" value={summary.naHands} color={COLORS.textMuted} />
      </Box>

      {/* Per-street accuracy */}
      <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", p: 2 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: COLORS.white, mb: 1.2 }}>
          <Box component="span" sx={{ color: COLORS.green, mr: 0.8 }}>●</Box>
          GTO accuracy by street
        </Typography>
        {["preflop", "flop", "turn", "river"].map((st) => {
          const row = summary.byStreet[st] || {};
          const score = row.avgScore;
          return (
            <Box key={st} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.8 }}>
              <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted, width: 60, textTransform: "capitalize" }}>{st}</Typography>
              <Box sx={{ flex: 1, height: 8, backgroundColor: "#2a2a2a", borderRadius: 999, overflow: "hidden" }}>
                <Box sx={{ width: `${score || 0}%`, height: "100%", backgroundColor: score == null ? "transparent" : score >= 80 ? COLORS.green : score >= 55 ? COLORS.white : COLORS.danger, borderRadius: 999, transition: "width 300ms ease" }} />
              </Box>
              <Typography sx={{ fontSize: 11.5, color: score == null ? COLORS.textMuted : COLORS.text, width: 40, textAlign: "right" }}>
                {score == null ? "-" : score}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, width: 130, textAlign: "right" }}>
                {row.decisions || 0} decisions · {row.blunders || 0} blunders
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Biggest leaks */}
      <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: COLORS.white }}>
            <Box component="span" sx={{ color: COLORS.danger, mr: 0.8 }}>●</Box>
            Hands with blunders
          </Typography>
          <Button size="small" onClick={onSeeHands} sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, color: COLORS.green, borderRadius: "999px", px: 1.4, backgroundColor: "rgba(255,255,255,0.03)", "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}>
            All hands →
          </Button>
        </Box>
        {blunderRows.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>No blunders found, clean play so far.</Typography>
        ) : (
          blunderRows.map((h) => (
            <Box key={h.handId} sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 0.6, borderBottom: `1px solid ${COLORS.border}`, "&:last-child": { borderBottom: "none" } }}>
              <Box sx={{ display: "flex", gap: 0.3 }}>
                {(h.heroCards || []).map((c, i) => (
                  <MiniCard key={i} card={c} size={0.9} />
                ))}
              </Box>
              <Typography sx={{ fontSize: 11.5, color: COLORS.text, width: 40 }}>{POSITION_LABELS[h.heroPos] || "?"}</Typography>
              <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted, flex: 1 }}>{h.potType} · {fmtDate(h.playedAt)}</Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.danger }}>{h.counts?.blunders} blunder{Number(h.counts?.blunders) > 1 ? "s" : ""}</Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: Number(h.winLossBb) >= 0 ? COLORS.green : COLORS.danger, width: 66, textAlign: "right" }}>
                {fmtBb(h.winLossBb)}bb
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

// ─── Hands list ──────────────────────────────────────────────────────────────

const HEAD_CELL = { fontSize: 10.5, fontWeight: 800, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" };

function HandsListView({ hands, loading, onOpen }) {
  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: COLORS.green }} />
      </Box>
    );
  }
  if (!hands.length) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: 13, color: COLORS.textMuted }}>No hands uploaded yet.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", overflow: "hidden" }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 900 }}>
          {/* Header row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "34px 62px 44px 64px 110px 64px 90px 70px 70px 70px 64px 70px 74px", gap: 1, alignItems: "center", px: 1.6, py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
            <Typography sx={HEAD_CELL}> </Typography>
            <Typography sx={HEAD_CELL}>Date</Typography>
            <Typography sx={HEAD_CELL}>Pos</Typography>
            <Typography sx={HEAD_CELL}>Hand</Typography>
            <Typography sx={HEAD_CELL}>Board</Typography>
            <Typography sx={HEAD_CELL}>Pot type</Typography>
            <Typography sx={HEAD_CELL}>Preflop</Typography>
            <Typography sx={HEAD_CELL}>Flop</Typography>
            <Typography sx={HEAD_CELL}>Turn</Typography>
            <Typography sx={HEAD_CELL}>River</Typography>
            <Typography sx={{ ...HEAD_CELL, textAlign: "right" }}>Pot (bb)</Typography>
            <Typography sx={{ ...HEAD_CELL, textAlign: "right" }}>W/L (bb)</Typography>
            <Typography sx={{ ...HEAD_CELL, textAlign: "right" }}>GTO Score</Typography>
          </Box>
          {hands.map((h) => {
            const isNa = h.status === "na";
            return (
              <Box
                key={h.handId}
                onClick={() => onOpen(h)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "34px 62px 44px 64px 110px 64px 90px 70px 70px 70px 64px 70px 74px",
                  gap: 1,
                  alignItems: "center",
                  px: 1.6,
                  py: 0.8,
                  cursor: "pointer",
                  borderBottom: `1px solid ${COLORS.border}`,
                  opacity: isNa ? 0.55 : 1,
                  transition: "background-color 120ms ease",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <StatusDot hand={h} />
                <Typography sx={{ fontSize: 11.5, color: COLORS.textMuted, whiteSpace: "nowrap" }}>{fmtDate(h.playedAt)}</Typography>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.text }}>{POSITION_LABELS[h.heroPos] || "?"}</Typography>
                <Box sx={{ display: "flex", gap: 0.3 }}>
                  {(h.heroCards || []).map((c, i) => (
                    <MiniCard key={i} card={c} size={0.85} />
                  ))}
                </Box>
                <Box sx={{ display: "flex", gap: 0.3 }}>
                  {(h.board || []).map((c, i) => (
                    <MiniCard key={i} card={c} size={0.85} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: 11.5, color: COLORS.text }}>{h.potType || "-"}</Typography>
                <StreetLetters txt={h.streetLines?.preflop} />
                <StreetLetters txt={h.streetLines?.flop} />
                <StreetLetters txt={h.streetLines?.turn} />
                <StreetLetters txt={h.streetLines?.river} />
                <Typography sx={{ fontSize: 11.5, color: COLORS.text, textAlign: "right" }}>{Number(h.potBb || 0).toFixed(1)}</Typography>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textAlign: "right", color: Number(h.winLossBb) > 0 ? COLORS.green : Number(h.winLossBb) < 0 ? COLORS.danger : COLORS.textMuted }}>
                  {fmtBb(h.winLossBb)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    textAlign: "right",
                    color: h.gtoScore == null ? COLORS.textMuted : h.gtoScore >= 80 ? COLORS.green : h.gtoScore >= 55 ? COLORS.white : COLORS.danger,
                  }}
                >
                  {h.gtoScore == null ? "-" : h.gtoScore}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
